import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, XIcon } from 'lucide-react';
import { CreditReport, Liability } from '../types/credit';
import { LiabilityTable } from './LiabilityTable';
import { PushBar } from './PushBar';
type LiabilityStepProps = {
  report: CreditReport;
  liabilities: Liability[];
  onBack: () => void;
  onPush: (ids: string[]) => Promise<void>;
};
export function LiabilityStep({
  report,
  liabilities,
  onBack,
  onPush
}: LiabilityStepProps) {
  const [isPushing, setIsPushing] = useState(false);
  const [pushedCount, setPushedCount] = useState<number | null>(null);
  const pending = liabilities.filter(l => !l.alreadyPushed && l.balance > 0);
  const excludedCount = liabilities.filter(l => !l.alreadyPushed && l.balance <= 0).length;
  const totalBalance = liabilities.reduce((sum, l) => sum + l.balance, 0);
  const totalMonthly = liabilities.reduce((sum, l) => sum + l.monthlyPayment, 0);
  const pendingBalance = pending.reduce((sum, l) => sum + l.balance, 0);
  const pendingMonthly = pending.reduce((sum, l) => sum + l.monthlyPayment, 0);
  const handlePush = async () => {
    const ids = pending.map(l => l.id);
    setIsPushing(true);
    await onPush(ids);
    setIsPushing(false);
    setPushedCount(ids.length);
  };
  return <>
      <section className="mx-auto w-full max-w-6xl px-6 pb-12 pt-6" aria-labelledby="liabilities-heading" data-id="element-5" data-mp="20mtf3t9zj9l3">
        <div className="flex flex-col gap-2" data-id="element-6" data-mp="1qx9uoj6mj7o4">
          <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 rounded text-sm font-medium text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2" data-id="element-7" data-mp="1qwhyp79477xb">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-8" data-mp="1czl7h4p33ofw" />
            Choose a different report
          </button>

          <h1 id="liabilities-heading" className="text-xl font-semibold leading-tight text-ink-900" data-id="element-9" data-mp="yateg8uh1001">
            Review liabilities on this report
          </h1>
          <p className="text-sm leading-tight text-ink-500" data-id="element-10" data-mp="oclz1uftyq05">
            From {report.bureauLabel} · {report.provider} · pulled{' '}
            {report.pulledOn}. Every liability with a balance is pushed to
            Encompass together.
          </p>
        </div>

        <AnimatePresence initial={false} data-id="element-11" data-mp="1h22vd36kmr1n">
          {pushedCount !== null && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} className="mt-6 flex items-start justify-between gap-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3" role="status" data-id="element-12" data-mp="1lz2v6wctuxkb">
              <div className="flex items-start gap-3" data-id="element-13" data-mp="24r5b25hna9ti">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-green-700" aria-hidden="true" data-id="element-14" data-mp="24sou23uoqyyz" />
                <div data-id="element-15" data-mp="3khen0qktwzc1">
                  <p className="text-sm font-semibold leading-tight text-green-900" data-id="element-16" data-mp="3khxv7fkog9cv">
                    {pushedCount}{' '}
                    {pushedCount === 1 ? 'liability' : 'liabilities'} pushed to
                    Encompass
                  </p>
                  <p className="mt-0.5 text-sm leading-tight text-green-800" data-id="element-17" data-mp="36hn205vwaioj">
                    They&apos;re marked below and won&apos;t be sent twice.
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setPushedCount(null)} aria-label="Dismiss confirmation" className="-mr-1 rounded p-1 text-green-800 transition-colors hover:bg-green-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-700" data-id="element-18" data-mp="3fk660tbf31cd">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-19" data-mp="pvvqygvgovv8" />
              </button>
            </motion.div>}
        </AnimatePresence>

        <div className="mt-6" data-id="element-20" data-mp="1q296trffax4i">
          <LiabilityTable liabilities={liabilities} pendingCount={pending.length} excludedCount={excludedCount} totalBalance={totalBalance} totalMonthly={totalMonthly} data-id="element-21" data-mp="1q3kgu8zgp142" />
        </div>
      </section>

      <PushBar count={pending.length} totalBalance={pendingBalance} totalMonthly={pendingMonthly} isPushing={isPushing} onPush={handlePush} data-id="element-22" data-mp="243lhbnbte6j0" />
    </>;
}